/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { Fragment, useCallback, useMemo } from 'react';
import { GroupTitle } from './GroupTitle';
import { QuestionList } from './QuestionList';
import { QuestionListEmpty } from './QuestionListEmpty';
import { QuestionListColumn, SurveyQuestionListProps } from '../survey-question-list';
import { BulkActionToolbarSelectedUtrv } from './BulkActionToolbar';
import { getQuestionGroupId, getTotalQuestionCount, HighlightQuestion } from '../utils';
import { BaseScopeQuestion } from '../../../types/surveyScope';
import { GroupSelectbox } from './GroupSelectBox';
import { useScrollToElement } from '../hooks/useScrollToElement';

interface QuestionListGroupsProps extends Pick<SurveyQuestionListProps,
  'surveyGroups' |
  'columns' |
  'alternativeCode' |
  'surveyId' |
  'initiativeId'
> {
  isHidden: (question: BaseScopeQuestion) => boolean;
  toggleSelectQuestion: (utrv: BulkActionToolbarSelectedUtrv) => void;
  columns?: QuestionListColumn[];
  isQuestionSelected: (utrvId: string) => boolean;
  toggleQuestionGroup: (questionGroupId: string) => void;
  enableBulkActions: boolean;
  highlightQuestion?: HighlightQuestion;
  questionGroupDisplay: string[];
  isBookmarked: (utrvId: string) => boolean,
  toggleBookmark: (utrvId: string) => void,
  selectedQuestions: BulkActionToolbarSelectedUtrv[];
  toggleSelectQuestions: (utrvs: BulkActionToolbarSelectedUtrv[]) => void;
  expandedQuestionGroupId?: string;
  resetExpandedQuestionGroup?: () => void;
};

export const QuestionListGroups = (props: QuestionListGroupsProps) => {
  const {
    surveyGroups,
    toggleQuestionGroup,
    alternativeCode,
    questionGroupDisplay,
    highlightQuestion,
    isHidden,
    enableBulkActions,
    selectedQuestions,
    toggleSelectQuestions,
    expandedQuestionGroupId,
    resetExpandedQuestionGroup,
  } = props;

  const singleGroup = useMemo(() => surveyGroups.length === 1, [surveyGroups]);
  const questionCount = useMemo(() => surveyGroups.reduce((acc, questionGroup) => acc + questionGroup.list.length, 0), [surveyGroups]);
  const { elementRef: questionGroupRef } = useScrollToElement(resetExpandedQuestionGroup);

  const isGroupOpen = useCallback((questionGroupId: string) => {
    return singleGroup || questionGroupDisplay.includes(questionGroupId);
  }, [singleGroup, questionGroupDisplay]);

  if (questionCount === 0) {
    return <QuestionListEmpty />;
  }

  const selectedQuestionIds = selectedQuestions.map(q => q._id);
  let startIndex = 0;
  return <>
    {surveyGroups.map((questionGroup, i) => {
      const questionList = questionGroup.list;
      const questionCount = getTotalQuestionCount(questionGroup);
      const isRenderedTitle = questionCount > 0;
      const questionGroupId = getQuestionGroupId(i);
      const isOpen = isGroupOpen(questionGroupId);
      const highlightRowRef = highlightQuestion?.groupId === questionGroupId ? highlightQuestion.rowRef : undefined;
      startIndex = startIndex + questionCount;
      let subGroupCountAccumulator = 0;
      const groupRef = questionGroupId === expandedQuestionGroupId ? questionGroupRef : undefined;

      return (
        <Fragment key={`group-${i}-${questionGroup.groupName}`}>
          {isRenderedTitle ? (
            <GroupTitle
              rowRef={groupRef || highlightRowRef}
              isOpen={isOpen}
              toggle={singleGroup ? undefined : () => toggleQuestionGroup(questionGroupId)}
              questionGroup={questionGroup}
              questionList={questionList}
              enableBulkActions={enableBulkActions}
              selectedQuestionIds={selectedQuestionIds}
              toggleSelectQuestions={toggleSelectQuestions}
            />
          ) : null}
          {isOpen ? (
            questionGroup.subGroups ? (
              questionGroup.subGroups.map((subGroup, ix) => {
                if (!subGroup.list) {
                  return null;
                }

                const questionList = subGroup.list.filter(q => !isHidden(q));
                if (questionList.length === 0) {
                  subGroupCountAccumulator += subGroup.list.length;
                  return null;
                }

                // calculate sub group index since it counts from zero
                const subGroupStartIndex = (startIndex - questionCount) + subGroupCountAccumulator;
                subGroupCountAccumulator += subGroup.list.length;

                return <Fragment key={`subgroup-${ix}-${subGroup.code}`}>
                  {subGroup.code !== '' ? (
                    // Questions that don't have sub-group tags associated
                    <div className='ml-3 sub__group__title d-flex align-items-center justify-content-between'>
                      <div>{subGroup.name}</div>
                      {enableBulkActions ? (
                        <GroupSelectbox
                          questionGroup={subGroup}
                          questionList={questionList}
                          selectedQuestionIds={selectedQuestionIds}
                          toggleSelectQuestions={toggleSelectQuestions}
                        />
                      ) : null}
                    </div>) : null}
                  <QuestionList
                    {...props}
                    questionGroupId={questionGroupId}
                    questionList={questionList}
                    startIndex={subGroupStartIndex}
                    alternativeCode={
                      alternativeCode ?? questionGroup.groupData?.alternativeCode ?? questionGroup.groupCode
                    }
                    preferredAltCodes={questionGroup.groupData?.preferredAltCodes}
                    colour={questionGroup.groupData && questionGroup.groupData.colour}
                    rowRef={highlightRowRef}
                  />
                </Fragment>
              }
              )
            ) : (
              <QuestionList
                {...props}
                questionGroupId={questionGroupId}
                questionList={questionList}
                startIndex={startIndex - questionCount}
                alternativeCode={alternativeCode ?? questionGroup.groupData?.alternativeCode ?? questionGroup.groupCode}
                preferredAltCodes={questionGroup.groupData?.preferredAltCodes}
                colour={questionGroup.groupData && questionGroup.groupData.colour}
                rowRef={highlightRowRef}
              />
            )
          ) : null}
        </Fragment>
      );
    })}
  </>;
}
